import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, concatLatestFrom, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TuiMobileDialogService, TuiSheetService } from '@taiga-ui/addon-mobile';
import { TuiSheetOptions } from '@taiga-ui/addon-mobile/components/sheet/sheet-options';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { ENVIRONMENT } from '@us/app-config';
import { selectIsTokenValid, selectPersistedUserId, selectSessionUser, selectUser } from '@us/web/auth/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { tuiIsPresent } from '@taiga-ui/cdk';
import { TuiAlertService, TuiDialogService } from '@taiga-ui/core';
import { TrackerService } from '@us/web/analytics/feature';
import { OrdersApiService } from '@us/web/orders/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OrderTypeSelectorComponent, QrPaymentComponent } from '@us/web/orders/ui';
import {
    BusinessModel,
    Customer,
    OrderItem,
    OrderShop,
    OrderState,
    OrderType,
    ProductType,
    Shop,
    Table
} from '@us/web/shared/data-access';
import { MessageService } from '@us/web/shared/feature';
import {
    getErrorMessage,
    getOrderPaymentQrCode,
    getOrderPaymentUrl,
    isFnBApp,
    parseDataServer,
    PROMOTION_SUBTYPE,
    PROMOTION_SUBTYPE2,
    PROMOTION_TYPE,
    RouteUtil
} from '@us/web/shared/utils';
import {
    selectRequiredToLogin,
    selectShopWithoutDefaultValue,
    selectTurnOnTheStoreChainLinkFeature
} from '@us/web/shop/data-access';
import { selectSelectedShop } from '@us/web/shops/data-access';
import { includes, isNil, omit, pick, uniqueId } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, combineLatestWith, concatMap, filter, map, switchMap, tap } from 'rxjs/operators';
import { CartApiService } from '../services/cart-api.service';
import { CartService } from '../services/cart.service';
import { CartPageStore } from './cart-page.store';
import { CartActions, CartEffectInitAction } from './cart.actions';
import {
    Cart,
    CartItem,
    getEmptyCart,
    selectCart,
    selectCartMembers,
    selectOrderId,
    selectOrderType
} from './cart.reducer';
import { PusherEvent } from '@us/web/websockets/data-access';

function getShopValue(shop: Shop | null | undefined): Shop | undefined {
    return shop
        ? (pick(shop, ['id', 'name', 'code', 'address', 'location', 'logo_url', 'tel', 'email']) as Shop)
        : undefined;
}

@Injectable()
export class CartEffects implements OnInitEffects {
    private readonly actions$ = inject(Actions);
    private readonly cartApiService = inject(CartApiService);
    private readonly cartService = inject(CartService);
    private readonly orderService = inject(OrdersApiService);
    private readonly translocoService = inject(TranslocoService);
    private readonly store = inject(Store);
    private readonly messageService = inject(MessageService);
    // private readonly messagingService = inject(MessagingService);
    // private readonly authService = inject(AuthService);
    private readonly navController = inject(NavController);
    private readonly sheetService = inject(TuiSheetService);
    private readonly injector = inject(Injector);
    private readonly cartStore = inject(CartPageStore);
    private readonly env = inject(ENVIRONMENT);
    private readonly router = inject(Router);
    private readonly mobileDialogService = inject(TuiMobileDialogService);
    private readonly alertService = inject(TuiAlertService);
    private readonly trackerService = inject(TrackerService);
    private readonly dialogService = inject(TuiDialogService);

    ngrxOnInitEffects(): Action {
        return CartEffectInitAction();
    }

    handleCartEffectAction$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(CartEffectInitAction),
                switchMap(() => this.store.select(selectIsTokenValid)),
                filter(valid => valid),
                switchMap(() => this.store.select(selectOrderId)),
                filter(orderId => !orderId),
                tap(() => {
                    this.store.dispatch(CartActions.resetCartState());
                    this.store.dispatch(CartActions.createCart());
                })
            );
        },
        { dispatch: false }
    );

    pusherEventsHandler$ = createEffect(
        () => {
            return this.cartService.orderUpdated$.pipe(
                combineLatestWith(this.store.select(selectPersistedUserId)),
                tap(([data, userId]) => {
                    const cart = data.cart;
                    if (cart.is_shared && !cart?.group_members.find(user => user.id === userId)) {
                        // trường hợp user bị đá ra khỏi nhóm
                        this.store.dispatch(CartActions.clearCart({}));
                        this.cartService.cartChannel()?.unbind(PusherEvent.CART_UPDATED);
                        return;
                    }
                    const cartUpdated = this.store.selectSignal(selectCart)();
                    const updated_at1 = cartUpdated.updated_at ?? 0;
                    const updated_at2 = cart.updated_at ?? 1;
                    if (updated_at2 > updated_at1 && cart.is_shared) {
                        this.store.dispatch(CartActions.updateCartSuccess({ cart: data.cart }));
                    }
                })
            );
        },
        { dispatch: false }
    );

    createCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.createCart),
            switchMap(() => {
                const cart = omit(this.store.selectSignal(selectCart)(), [
                    'order_id',
                    'productList',
                    'type',
                    'group_members'
                ]) as Cart;
                cart.shop = getShopValue(cart.shop || this.store.selectSignal(selectShopWithoutDefaultValue)());

                return this.cartApiService.createCart(cart).pipe(
                    map(response => CartActions.createCartSuccess({ cart: response })),
                    catchError(error => of(CartActions.createCartFailed({ error: getErrorMessage(error) })))
                );
            })
        );
    });

    getCartDetail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.getCartDetail),
            concatLatestFrom(() => this.store.select(selectOrderId)),
            switchMap(([, id]) => {
                return this.cartApiService.getCart(id).pipe(
                    map(response => {
                        if (
                            includes(
                                [OrderState.Canceled, OrderState.Rejected, OrderState.Complete],
                                response.current_state
                            )
                        ) {
                            return CartActions.clearCart({ shop: response.shop });
                        }

                        return CartActions.getCartDetailSuccess({ cart: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error.status === 404) {
                            return of(CartActions.createCart());
                        }
                        return of(CartActions.getCartDetailFailed({ error: getErrorMessage(error) }));
                    })
                );
            })
        );
    });

    clearCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.clearCart),
            switchMap(({ item, shop }) => {
                const orderType = (this.store.selectSignal(selectOrderType)() || null) as OrderType | null;

                const payload = {
                    ...getEmptyCart({
                        order_type: orderType,
                        shop: getShopValue(shop || this.store.selectSignal(selectShopWithoutDefaultValue)())
                    }),
                    items: item ? [item] : []
                };

                localStorage.removeItem('is_merge_order_qr');

                return this.cartApiService.createCart(payload).pipe(
                    map(response => CartActions.clearCartSuccess({ cart: response })),
                    catchError(error => of(CartActions.clearCartFailed({ error: getErrorMessage(error) })))
                );
            })
        );
    });

    updateCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.updateCart),
            concatLatestFrom(() => this.store.select(selectOrderId)),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            switchMap(([{ type, ...payload }, id]) => {
                // const id = this.store.selectSignal(selectOrderId)();
                if (!id) return [];
                return this.cartApiService.updateCart(id, payload).pipe(
                    map(response => CartActions.updateCartSuccess({ cart: response })),
                    catchError(error => of(CartActions.updateCartFailed({ error: getErrorMessage(error) })))
                );
            })
        );
    });

    handleActionFailed$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(CartActions.updateCartFailed),
                tap(({ error }) => {
                    this.alertService.open(error, { status: 'error' }).subscribe();
                })
            );
        },
        { dispatch: false }
    );

    addToCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.addToCart),
            concatLatestFrom(() => [
                this.store.select(selectCart),
                // TODO: handle selectTurnOnTheStoreChainLinkFeature for FnB
                isFnBApp(this.env)
                    ? this.store.select(selectShopWithoutDefaultValue).pipe(filter(tuiIsPresent))
                    : this.store.select(selectTurnOnTheStoreChainLinkFeature).pipe(
                          filter(tuiIsPresent),
                          switchMap(chain =>
                              chain
                                  ? this.store.select(selectSelectedShop)
                                  : this.store.select(selectShopWithoutDefaultValue)
                          ),
                          filter(tuiIsPresent)
                      )
            ]),
            concatMap(([{ item, redirectUrl }, cart, shop]) => {
                if (includes([OrderState.Canceled, OrderState.Rejected, OrderState.Complete], cart.current_state)) {
                    return of(
                        CartActions.clearCart({
                            item,
                            shop: getShopValue(shop)
                        })
                    );
                }
                if (cart.shop && cart.shop.id !== shop?.id) {
                    //     Swal.fire({
                    //         title: 'Bạn muốn đặt món ở cửa hàng này',
                    //         text: 'Những món bạn đã đặt ở của hàng trước sẽ bị xóa khỏi giỏ hàng',
                    //         showCancelButton: true,
                    //         confirmButtonText: 'Tiếp tục',
                    //         denyButtonText: `Trờ về`,
                    //     })

                    const msg =
                        'Bạn muốn đặt món ở cửa hàng này? Những món bạn đã đặt ở của hàng trước sẽ bị xóa khỏi giỏ hàng';
                    // eslint-disable-next-line no-restricted-globals
                    if (confirm(msg)) {
                        return of(
                            CartActions.clearCart({
                                item,
                                shop: getShopValue(shop)
                            })
                        );
                    }

                    return of(CartActions.updateCartFailed({ error: 'cancel' }));
                }
                return (
                    ((isNil(cart.order_type) || (cart.order_type === OrderType.OrderAtTheTable && !cart.order_table)) &&
                    this.env.appType === 'fnb'
                        ? this.sheetService.open(new PolymorpheusComponent(OrderTypeSelectorComponent, this.injector), {
                              closeable: true,
                              overlay: true
                          } as Partial<TuiSheetOptions>)
                        : of({
                              orderType: cart.order_type,
                              table: cart.order_table,
                              is_shared: cart.is_shared
                          })) as Observable<{
                        orderType: OrderType | null;
                        table: Table | undefined;
                        is_shared: boolean | undefined;
                    }>
                ).pipe(
                    switchMap(({ orderType, table, is_shared }) => {
                        if (
                            this.env.appType === 'fnb' &&
                            (!orderType || (orderType === OrderType.OrderAtTheTable && !table))
                        ) {
                            return of(CartActions.updateCartFailed({ error: 'missing table' }));
                        }
                        const id = this.store.selectSignal(selectOrderId)();
                        const user = this.store.selectSignal(selectSessionUser)();
                        const userOrder = this.store.selectSignal(selectUser)(); // user has login
                        const currentUserId = this.store.selectSignal(selectPersistedUserId)();
                        const members = this.store.selectSignal(selectCartMembers)();
                        const owner = computed(() => members.find(member => member.is_owner))();
                        const isOwner = computed(() => currentUserId === owner?.id)();
                        const params: {
                            item: CartItem;
                            shop?: OrderShop;
                            order_type?: OrderType;
                            table?: Table;
                            customer?: Customer;
                        } = {
                            item: {
                                ...item,
                                user
                            },
                            shop: getShopValue(shop),
                            order_type: orderType || undefined,
                            table
                        };
                        if (!is_shared && userOrder) {
                            params['customer'] = {
                                id: userOrder.id,
                                name: userOrder.name,
                                phone_number: userOrder.phone,
                                email: userOrder.email
                            };
                        } else if (isOwner && userOrder) {
                            params['customer'] = {
                                id: userOrder.id,
                                name: userOrder.name,
                                phone_number: userOrder.phone,
                                email: userOrder.email
                            };
                        }
                        return this.cartApiService.addItemToCart(id, params).pipe(
                            map(response => {
                                let msg: string;
                                if (item.uuid) {
                                    msg = 'update_success';
                                } else if (item.quantity > 0) {
                                    msg = 'added_to_cart';
                                } else {
                                    msg = 'removed_from_cart';
                                }
                                this.messageService.success(this.translocoService.translate(msg));
                                this.cartStore.setCurrentTabIndex(0);
                                if (redirectUrl) {
                                    if (redirectUrl.includes('?pageYOffset=')) {
                                        const query = redirectUrl.split('=')[1] ?? 0;
                                        this.router.navigate([redirectUrl], {
                                            queryParams: {
                                                pageYOffset: query
                                            }
                                        });
                                    } else {
                                        this.router.navigate([redirectUrl]);
                                    }
                                }
                                return CartActions.addToCartSuccess({ cart: response });
                            }),
                            catchError(error => of(CartActions.updateCartFailed({ error: getErrorMessage(error) })))
                        );
                    })
                );
            })
        );
    });

    updateItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.updateItemQuantity),
            switchMap(({ item, quantity }) => {
                const id = this.store.selectSignal(selectOrderId)();

                return this.cartApiService.updateItem(id, item.uuid as string, { quantity }).pipe(
                    map(response => CartActions.updateCartSuccess({ cart: response })),
                    catchError(error => {
                        const message = getErrorMessage(error, this.translocoService.translate('message.error'));
                        return of(CartActions.updateCartFailed({ error: message }));
                    })
                );
            })
        );
    });

    removeFromCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.removeFromCart),
            concatMap(({ item: { uuid } }) => {
                const id = this.store.selectSignal(selectOrderId)();

                return this.cartApiService.removeItemFromCart(id, uuid as string).pipe(
                    map(response => {
                        this.messageService.warning(this.translocoService.translate('removed_from_cart'));
                        return CartActions.removeFromCartSuccess({ cart: response });
                    }),
                    catchError(error => {
                        const message = getErrorMessage(error, this.translocoService.translate('message.error'));
                        this.messageService.error(message);
                        return of(CartActions.removeFromCartFailed({ error: message }));
                    })
                );
            })
        );
    });

    removeCartMember$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.removeCartMember),
            concatMap(({ userId }) => {
                const cartId = this.store.selectSignal(selectOrderId)();
                const currentUserId = this.store.selectSignal(selectPersistedUserId)();
                const selfRemove = currentUserId === userId;
                return this.cartApiService.removeCartMember(cartId, userId).pipe(
                    map(response =>
                        CartActions.removeCartMemberSuccess({ cart: selfRemove ? getEmptyCart() : response })
                    ),
                    catchError(error => {
                        const message = getErrorMessage(error, this.translocoService.translate('message.error'));
                        this.messageService.error(message);
                        return of(CartActions.removeCartMemberFailed({ error: message }));
                    })
                );
            })
        );
    });

    // addToCartSuccess$ = createEffect(
    //     () => {
    //         return this.actions$.pipe(
    //             ofType(CartActions.addToCartSuccess),
    //             tap(({ item }) => {
    //                 this.trackerService.addToCart(item);
    //             })
    //         );
    //     },
    //     { dispatch: false }
    // );

    // selectShop$ = createEffect(() => {
    //     return this.actions$.pipe(
    //         ofType(StoresActions.selectStore),
    //         concatLatestFrom(() => [this.store.select(selectSelectedStore), this.store.select(selectCartShop)]),
    //         concatMap(([, shop, currentShop]) => {
    //             if (currentShop !== undefined && (shop as Shop).id !== currentShop?.id) {
    //                 // TODO: confirm
    //                 this.navController.back();
    //                 return of(CartActions.clearCart({ shop: shop as Shop }));
    //             }
    //             this.navController.back();
    //             return of(CartActions.updateCart({ shop: shop as Shop }));
    //         })
    //     );
    // });

    selectTable$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.selectTable),
            concatLatestFrom(() => this.store.select(selectOrderId)),
            concatMap(([{ tableId }, cartId]) => {
                const user = this.store.selectSignal(selectSessionUser)();
                return this.cartApiService.selectTable(cartId, { table_id: tableId, user }).pipe(
                    map(response => {
                        let data: any = response;
                        if (data.is_merge_order_qr) {
                            localStorage.setItem('is_merge_order_qr', data.is_merge_order_qr);
                            data = data.cart;
                        }
                        this.messageService.info(
                            this.translocoService.translate('chose_table') + ' ' + data.order_table?.name
                        );
                        return CartActions.selectTableSuccess({ cart: parseDataServer(data) });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.mobileDialogService
                            .open(getErrorMessage(error), {
                                label: this.translocoService.translate('notification'),
                                actions: [this.translocoService.translate('ok')]
                            })
                            .subscribe();
                        return of(CartActions.selectTableFailed({ error: getErrorMessage(error) }));
                    }),
                    tap(() => {
                        this.router.navigate([], {
                            queryParams: {
                                tableId: null
                            },
                            queryParamsHandling: 'merge'
                        });
                    })
                );
            })
        );
    });

    joinSharedOrder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.joinSharedOrder),
            concatMap(({ cartId, user, onSuccess, onError }) =>
                this.cartApiService.joinSharedOrder(cartId, user).pipe(
                    map(response => {
                        onSuccess?.();
                        return CartActions.joinSharedOrderSuccess({ cart: response });
                    }),
                    catchError(error => {
                        onError?.(getErrorMessage(error));
                        return of(CartActions.joinSharedOrderFailed({ error: getErrorMessage(error) }));
                    })
                )
            )
        );
    });

    submitCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.submitCart),
            concatLatestFrom(() => [
                this.store.select(selectCart),
                this.store.select(selectUser),
                this.store.select(selectShopWithoutDefaultValue),
                this.store.select(selectRequiredToLogin)
            ]),
            concatMap(([{ businessModel, paymentType }, cart, user, shop, requiredToLogin]) => {
                try {
                    if (
                        !user &&
                        requiredToLogin &&
                        cart.order_type !== OrderType.TakeAway &&
                        cart.order_type !== OrderType.OrderAtTheTable
                    ) {
                        throw new Error('unauthorized');
                    }

                    const cartId = this.store.selectSignal(selectOrderId)();

                    this.cartApiService.validateOrder(cart);
                    return this.cartApiService
                        .submitCart(cartId, {
                            business_model: businessModel,
                            payment_type: paymentType
                        })
                        .pipe(
                            map(response => {
                                return CartActions.submitCartSuccess({ cart: response, businessModel });
                            }),
                            catchError(error => of(CartActions.submitCartFailed({ error })))
                        );
                } catch (error) {
                    console.error(error);
                    return of(CartActions.submitCartFailed({ error: getErrorMessage(error) }));
                }
            }),
            catchError(error => {
                console.error(error);
                return of(CartActions.submitCartFailed({ error: getErrorMessage(error) }));
            })
        );
    });

    submitCartSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(CartActions.submitCartSuccess),
                tap(async ({ cart: value, businessModel }) => {
                    if (this.router.url.includes('/cart/checkout')) {
                        await this.navController.navigateBack(RouteUtil.getCartRouteUrl());
                    }

                    this.trackerService.purchase(value);
                    const orderAtTheTable = value.order_type === OrderType.OrderAtTheTable;

                    if (!orderAtTheTable || businessModel !== BusinessModel.PrePayment || value.version_code === 1) {
                        this.messageService.success(
                            this.translocoService.translate(orderAtTheTable ? 'pos.order_placed' : 'order_placed')
                        );
                    }

                    if (
                        [
                            OrderState.Canceled,
                            OrderState.Rejected,
                            OrderState.Complete,
                            OrderState.PendingPayment,
                            OrderState.PaymentSuccess,
                            OrderState.PaymentFailed
                        ].includes(value.current_state) ||
                        ![OrderType.Delivery, OrderType.TakeAway, OrderType.OrderAtTheTable].includes(
                            value.order_type!
                        ) ||
                        businessModel === BusinessModel.PrePayment
                    ) {
                        await this.navController.navigateForward(RouteUtil.getOrderRouteUrl(value.order_id));
                    } else {
                        this.cartStore.setCurrentTabIndex(1);
                        await this.navController.navigateBack(RouteUtil.getCartRouteUrl());
                    }

                    const paymentUrl: string | null = getOrderPaymentUrl(value);

                    if (getOrderPaymentQrCode(value)) {
                        this.dialogService.open(new PolymorpheusComponent(QrPaymentComponent, this.injector), {
                              size: 's',
                              data: {
                                 payment_detail: value.payment_detail,
                                 order: value
                              },
                              closeable: false,
                              dismissible: false
                        }).subscribe();
                        return;
                    }

                    if (orderAtTheTable && !paymentUrl) { // không show lỗi khi order tại bàn xác nhận món
                        return;
                    }

                    if (!value?.payment_detail && [OrderType.Delivery, OrderType.Pickup].includes(value.order_type || 0)) {
                        return;
                    }

                    if (!paymentUrl) {
                        this.messageService.error(
                            value?.payment_detail?.message || this.translocoService.translate('message.error')
                        );
                        return;
                    }

                    window.location.href = paymentUrl;
                    return;
                })
            );
        },
        { dispatch: false }
    );

    submitCartFailed$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(CartActions.submitCartFailed),
                tap(async ({ error }) => {
                    if (error) {
                        if (getErrorMessage(error, '') === 'unauthorized') {
                            await this.navController.navigateForward(['/login'], {
                                queryParams: {
                                    context: 'checkout',
                                    returnUrl: '/checkout'
                                }
                            });
                        } else {
                            const message = getErrorMessage(error, this.translocoService.translate('message.error'));
                            this.messageService.error(message);
                        }
                    }
                })
            );
        },
        { dispatch: false }
    );

    pushOrderState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.pushOrderState),
            concatLatestFrom(() => this.store.select(selectOrderId)),
            concatMap(([{ new_state }, order_id]) => {
                return this.orderService.pushOrderState({ order_id, state: new_state }).pipe(
                    map(order => CartActions.pushOrderStateSuccess({ order: order as Cart })),
                    catchError(error => of(CartActions.pushOrderStateFailed({ error })))
                );
            }),
            catchError(error => of(CartActions.pushOrderStateFailed({ error })))
        );
    });

    orderCartItemDefault = (
        promotion_type: number,
        promotion_subtype: number | null,
        promotion_subtype2: number | null,
        product_id: string,
        product_name: string,
        amount: number,
        unit_id: number,
        unit_name: string,
        gift_food: number,
        discount_id = 0,
        surcharge_id = 0,
        reason = '',
        options: any = []
    ) => {
        const orderItemDefault: OrderItem = {
            uuid: uniqueId(),
            combo: [],
            extra_discount: {
                promotion_type: promotion_type,
                promotion_subtype: promotion_subtype,
                promotion_subtype2: promotion_subtype2,
                group_local_id: ''
            },
            note: '',
            specifications: '',
            options: options,
            price: 0,
            product_id: product_id,
            product_name: product_name,
            product_type: ProductType.Goods, // TODO
            quantity: amount,
            unit: {
                id: unit_id,
                is_primary: false,
                name: unit_name,
                old_price: 0,
                price: 0,
                ratio: 1
            },
            discount: {
                id: discount_id,
                price: 0,
                percent: 0,
                reason: discount_id ? reason : ''
            },
            surcharge: {
                id: surcharge_id,
                price: 0,
                percent: 0,
                reason: surcharge_id ? reason : ''
            },
            gift_food: gift_food,
            version_code: 0
        };
        return orderItemDefault;
    };
}
